import styled, { css } from 'styled-components';
import {
  ContainerProps,
  DeleteButtonProps,
  FileUploadContainerProps,
  FileUploadLoaderContainerProps,
} from './props';
import Color from 'color';

import { Image as NextImage } from '@starsoft/common/components';
import { getComputedColor } from '@/utils/theme';

export const IconUpload = styled.i`
  font-size: 28px;
`;

export const Container = styled.label<ContainerProps>`
  background-color: ${({ theme }) => theme.vars.palette.background.default};
  padding: 1rem 1rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.25s ease-in-out;

  &:hover {
    background-color: ${({ theme }) =>
      Color(getComputedColor(theme.vars.palette.background.default))
        .alpha(0.6)
        .toString()};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    transition: all 0.25s ease-in-out;

    ${({ isDragging }) =>
      isDragging &&
      css`
        width: 100%;
        height: 100%;
        background-color: ${({ theme }) =>
          Color(getComputedColor(theme.vars.palette.background.paper))
            .alpha(0.6)
            .toString()};
        border-radius: ${({ theme }) => theme.vars.shape.borderRadius};
      `}
  }
`;

export const Input = styled.input`
  all: unset;
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;

  h5,
  p {
    font-size: 0.75rem;
    line-height: 0.9rem;
  }

  h5 {
    color: ${({ theme }) => theme.vars.palette.text.primary};
    font-weight: 600;
  }

  p {
    color: ${({ theme }) => theme.vars.palette.text.secondary};
    font-weight: 400;
  }
`;

export const FileUploadContainer = styled.div<FileUploadContainerProps>`
  width: 100%;
  display: flex;
  background-color: ${({ theme }) => theme.vars.palette.background.paper};
  padding: 0.5rem;
  border-radius: ${({ theme }) => theme.vars.shape.borderRadius};

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;

  position: relative;

  transition: all 0.3s;

  span {
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ theme, isLoading }) =>
      isLoading
        ? theme.vars.palette.text.secondary
        : theme.vars.palette.text.primary};

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 75%;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    border-radius: ${({ theme }) => theme.vars.shape.borderRadius};
    height: 2px;
    transition: width 0.25s;
    width: ${({ progress }) => (progress ? progress : 0)}%;
    background-color: ${({ theme }) => theme.vars.palette.primary.main};
  }
`;

export const FileUploadLoadingContainer = styled.div<FileUploadLoaderContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 44px;
  background-color: ${({ theme }) => theme.vars.palette.background.default};
  border-radius: ${({ theme }) => theme.vars.shape.borderRadius};
  position: relative;

  ${({ isComplete, theme }) =>
    isComplete &&
    css`
      &::before {
        content: '';
        border: 4px solid ${theme.vars.palette.primary.main};
        border-top: none;
        border-right: none;
        height: 10px;
        left: 13px;
        top: 15px;
        position: absolute;
        transform: rotate(-45deg);
        width: 20px;

        @media (max-width: 768px) {
          left: 12px;
        }
      }
    `}
`;

export const FileUploadWrapper = styled.div`
  width: 80%;
  display: flex;
  align-items: center;
  gap: 0.65rem;
`;

export const DeleteButton = styled.div<DeleteButtonProps>`
  width: 30px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) =>
    Color(getComputedColor(theme.vars.palette.text.secondary))
      .alpha(0.1)
      .toString()};
  cursor: pointer;
  color: ${({ theme }) => theme.vars.palette.text.secondary};
  font-size: 14px;
  transition: all 0.3s;
  border-radius: 50%;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};

  &:hover {
    background-color: ${({ theme }) =>
      Color(getComputedColor(theme.vars.palette.text.primary))
        .alpha(0.3)
        .toString()};
    color: ${({ theme }) => theme.vars.palette.text.primary};
  }

  & i {
    font-size: 0.7rem;
  }
`;

export const Spinner = styled.i`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.vars.palette.primary.main};
`;

export const PendingIcon = styled(Spinner)``;

export const ImagePreview = styled(NextImage)`
  width: 44px;
  height: 100%;
  border-radius: ${({ theme }) => theme.vars.shape.borderRadius};
  aspect-ratio: auto 44 / 44;
`;
